import React, { useState } from "react";
import {
  AHSeparator,
  AHText,
  Box,
  Circle,
  ListItem,
  EmployeeAvatar,
  Dialog,
} from "arkh";

import { graphql, useFragment, useLazyLoadQuery } from "react-relay";

import { nameMapper, useFilterSearch } from "@/src/components/mobile/useSearch";

import { SearchInput } from "@/src/features/Products/SearchInput";

import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { FlashList } from "@shopify/flash-list";
import { KeyboardAwareFlashListScrollComponent } from "@/src/components/mobile/KeyboardAwareFlashListScrollComponent";
import { onPressInHaptic } from "widgets/src/onPressInHaptic";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";

import { EmployeeRoleEditEmployeesDialog_EmployeeConnection$key } from "./__generated__/EmployeeRoleEditEmployeesDialog_EmployeeConnection.graphql";
import { EmployeeRoleEditEmployeesDialogQuery } from "./__generated__/EmployeeRoleEditEmployeesDialogQuery.graphql";
import { EmployeeRoleEditEmployeesDialog_EmployeeItem$key } from "./__generated__/EmployeeRoleEditEmployeesDialog_EmployeeItem.graphql";

type EmployeeRoleEditEmployeesDialogProps = {
  assignEmployeesIds: string[];
  setAssignEmployeesIds: (assignEmployeesIds: string[]) => void;
  teamId: string;
  disabled?: boolean;
  onClose: () => void;
  visible: boolean;
  title?: string;
  subtitle?: string;
  teamColor?: string | null;
};

export function EmployeeRoleEditEmployeesDialog(
  props: EmployeeRoleEditEmployeesDialogProps
) {
  const intl_tokens = useIntlTokens();
  return (
    <Dialog
      visible={props.visible}
      onClose={props.onClose}
      height={"80%"}
      maxHeight={600}
      title={
        !props.subtitle ? (props.title ?? intl_tokens.Assignees) : undefined
      }
      customTitle={
        props.subtitle ? (
          <Box>
            <AHText type="title-small" color="text-secondary">
              {props.title ?? intl_tokens.Assignees}
            </AHText>
            <Box style={{ flexDirection: "row", alignItems: "center" }}>
              <AHText type="body" color="text-secondary" spaceRight="xsmall">
                {props.subtitle}
              </AHText>
              <Circle
                size="dot"
                style={{ backgroundColor: props.teamColor ?? "#888" }}
              />
            </Box>
          </Box>
        ) : undefined
      }
    >
      <LazyQueryNetworkBoundrary>
        {(fetchKey) => (
          <EmployeeRoleEditEmployeesDialogContent
            assignEmployeesIds={props.assignEmployeesIds}
            setAssignEmployeesIds={props.setAssignEmployeesIds}
            teamId={props.teamId}
            disabled={props.disabled}
            fetchKey={fetchKey}
          />
        )}
      </LazyQueryNetworkBoundrary>
    </Dialog>
  );
}

function EmployeeRoleEditEmployeesDialogContent(
  props: Omit<EmployeeRoleEditEmployeesDialogProps, "onClose" | "visible"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<EmployeeRoleEditEmployeesDialogQuery>(
    graphql`
      query EmployeeRoleEditEmployeesDialogQuery($teamIds: [ID!]) {
        store @required(action: THROW) {
          employees(teamIds: $teamIds, sortByWorkingStatus: true) {
            ...EmployeeRoleEditEmployeesDialog_EmployeeConnection
          }
        }
      }
    `,
    {
      teamIds: [props.teamId],
    },
    {
      fetchPolicy: "store-and-network",
      fetchKey: props.fetchKey,
    }
  );
  return (
    <EmployeeRoleEmployeesDialogList
      assignEmployeesIds={props.assignEmployeesIds}
      setAssignEmployeesIds={props.setAssignEmployeesIds}
      disabled={props.disabled}
      employeeConnection={data.store.employees}
    />
  );
}

function EmployeeRoleEmployeesDialogList(props: {
  assignEmployeesIds: string[];
  setAssignEmployeesIds: (assignEmployeesIds: string[]) => void;
  disabled?: boolean;
  employeeConnection: EmployeeRoleEditEmployeesDialog_EmployeeConnection$key;
}) {
  const [query, setQuery] = useState("");
  const [initialAssignEmployeesIds] = useState(props.assignEmployeesIds);

  const employeeConnection = useFragment(
    graphql`
      fragment EmployeeRoleEditEmployeesDialog_EmployeeConnection on EmployeeConnection {
        edges {
          node {
            id
            name
            ...EmployeeRoleEditEmployeesDialog_EmployeeItem
          }
        }
      }
    `,
    props.employeeConnection
  );

  const employees =
    employeeConnection.edges
      .map((edge) => edge.node)
      .filter((node) => !!node) ?? [];

  const [_isSearching, filteredData] = useFilterSearch(
    query,
    employees,
    nameMapper
  );

  const sortedData = filteredData.slice().sort((a, b) => {
    const aIndex = initialAssignEmployeesIds.includes(a.id) ? 1 : 0;
    const bIndex = initialAssignEmployeesIds.includes(b.id) ? 1 : 0;
    return bIndex - aIndex;
  });

  type Employee = (typeof filteredData)[number];

  const renderItem = ({ item }: { item: Employee }) => {
    const selected = props.assignEmployeesIds.includes(item.id);
    return (
      <EmployeeItem
        disabled={props.disabled ?? false}
        employee={item}
        selected={selected}
        onPress={() => {
          onPressInHaptic();

          props.setAssignEmployeesIds(
            props.assignEmployeesIds.includes(item.id)
              ? props.assignEmployeesIds.filter((id) => id !== item.id)
              : [...props.assignEmployeesIds, item.id]
          );
        }}
      />
    );
  };

  return (
    <>
      <AHSeparator />
      <Box style={{ flex: 1 }}>
        <Box color="surface">
          <SearchInput
            isSearching
            query={query}
            onChange={(e) => setQuery(e.nativeEvent.text)}
            returnKeyType="done"
            autoCapitalize="words"
          />
        </Box>

        <FlashList
          data={sortedData}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          ItemSeparatorComponent={AHSeparator}
          renderScrollComponent={KeyboardAwareFlashListScrollComponent}
        />
      </Box>
    </>
  );
}

function EmployeeItem(props: {
  employee: EmployeeRoleEditEmployeesDialog_EmployeeItem$key;
  selected: boolean;
  onPress: (employeeId: string) => void;
  disabled: boolean;
}) {
  const employee = useFragment(
    graphql`
      fragment EmployeeRoleEditEmployeesDialog_EmployeeItem on Employee {
        id
        name

        ...Avatar_employee
      }
    `,
    props.employee
  );
  return (
    <ListItem
      key={employee.id}
      disabled={props.disabled}
      onPress={() => props.onPress(employee.id)}
      left={<EmployeeAvatar employee={employee} size="large" />}
      right={<ListItem.SelectIndicator selected={props.selected} />}
    >
      <ListItem.Title>{employee.name}</ListItem.Title>
    </ListItem>
  );
}
